import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { LogOut, RefreshCw, Users, Download, KeyRound, X, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ccc_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filterDate, setFilterDate] = useState("all");
  const [search, setSearch] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [pwdMsg, setPwdMsg] = useState({ type: "", text: "" });

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setRows([]);
  }, []);

  const loadRsvps = useCallback(
    async (t) => {
      setLoading(true);
      setError("");
      try {
        const { data } = await axios.get(`${API}/admin/rsvps`, {
          headers: { Authorization: `Bearer ${t}` },
        });
        setRows(data);
      } catch (e) {
        if (e.response?.status === 401) logout();
        else setError("Could not load RSVPs. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [logout]
  );

  useEffect(() => {
    if (token) loadRsvps(token);
  }, [token, loadRsvps]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, creds);
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setError(err.response?.status === 401 ? "Invalid email or password." : "Login failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  const allDateLabels = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => (r.dates || []).forEach((d) => set.add(d)));
    return Array.from(set);
  }, [rows]);

  const filteredRows = useMemo(() => {
    let r = filterDate === "all" ? rows : rows.filter((x) => (x.dates || []).includes(filterDate));
    const q = search.trim().toLowerCase();
    if (q) {
      r = r.filter(
        (x) => (x.name || "").toLowerCase().includes(q) || (x.email || "").toLowerCase().includes(q)
      );
    }
    return r;
  }, [rows, filterDate, search]);

  const exportCsv = () => {
    const header = ["Name", "Email", "Cell number", "Dates", "Submitted"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [header.map(esc).join(",")];
    filteredRows.forEach((r) => {
      lines.push(
        [r.name, r.email, r.phone || "", (r.dates || []).join("; "), fmtDate(r.updated_at)].map(esc).join(",")
      );
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rsvps-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwdMsg({ type: "", text: "" });
    if (pwd.next.length < 8) return setPwdMsg({ type: "error", text: "New password must be at least 8 characters." });
    if (pwd.next !== pwd.confirm) return setPwdMsg({ type: "error", text: "New passwords do not match." });
    try {
      await axios.post(
        `${API}/admin/change-password`,
        { current_password: pwd.current, new_password: pwd.next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPwdMsg({ type: "ok", text: "Password updated." });
      setPwd({ current: "", next: "", confirm: "" });
    } catch (err) {
      setPwdMsg({ type: "error", text: err.response?.data?.detail || "Could not update password." });
    }
  };


  // ---------- LOGIN VIEW ----------
  if (!token) {
    return (
      <div className="min-h-screen bg-oat flex items-center justify-center px-5 py-16">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-md bg-mushroom-light rounded-[2rem] p-8 sm:p-10 space-y-6"
        >
          <div>
            <h1 className="font-display font-black lowercase tracking-tighter leading-[0.9] text-4xl sm:text-5xl text-ink">
              admin
            </h1>
            <p className="mt-2 text-base font-medium text-ink/60">coffee, create &amp; community</p>
          </div>

          <div>
            <label htmlFor="admin-email" className="block text-sm font-semibold text-ink/70 mb-2">
              email
            </label>
            <input
              id="admin-email"
              data-testid="admin-email"
              type="email"
              value={creds.email}
              onChange={(e) => setCreds({ ...creds, email: e.target.value })}
              className="w-full bg-oat rounded-xl px-5 py-4 text-lg font-medium text-ink outline-none border-2 border-transparent focus:border-ink transition-colors"
              placeholder="you@email.com"
            />
          </div>

          <div>
            <label htmlFor="admin-password" className="block text-sm font-semibold text-ink/70 mb-2">
              password
            </label>
            <input
              id="admin-password"
              data-testid="admin-password"
              type="password"
              value={creds.password}
              onChange={(e) => setCreds({ ...creds, password: e.target.value })}
              className="w-full bg-oat rounded-xl px-5 py-4 text-lg font-medium text-ink outline-none border-2 border-transparent focus:border-ink transition-colors"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <p className="text-base font-semibold text-red-700" data-testid="admin-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            data-testid="admin-login-btn"
            disabled={loading}
            className="w-full inline-flex items-center justify-center bg-sunbeam text-ink font-display font-black lowercase tracking-tight rounded-full px-8 py-4 text-lg leading-none shadow-[0_6px_0_0_#1C1B18] hover:shadow-[0_3px_0_0_#1C1B18] transition-shadow disabled:opacity-60"
          >
            {loading ? "signing in…" : "sign in"}
          </button>
        </form>
      </div>
    );
  }

  // ---------- DASHBOARD VIEW ----------
  return (
    <div className="min-h-screen bg-oat px-5 sm:px-8 lg:px-12 py-10 sm:py-14">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display font-black lowercase tracking-tighter leading-[0.85] text-4xl sm:text-6xl text-ink">
              rsvps
            </h1>
            <p className="mt-2 flex items-center gap-2 text-base font-semibold text-mushroom-deep" data-testid="admin-count">
              <Users className="w-5 h-5" strokeWidth={2.4} />
              {rows.length} {rows.length === 1 ? "response" : "responses"}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={exportCsv}
              data-testid="admin-export"
              className="inline-flex items-center gap-2 bg-butter text-ink font-semibold rounded-full px-5 py-3 hover:brightness-95 transition-all"
            >
              <Download className="w-4 h-4" strokeWidth={2.4} />
              export CSV
            </button>
            <button
              onClick={() => { setShowPwd((s) => !s); setPwdMsg({ type: "", text: "" }); }}
              data-testid="admin-change-password-toggle"
              className="inline-flex items-center gap-2 bg-mushroom-light text-ink font-semibold rounded-full px-5 py-3 hover:bg-mushroom transition-colors"
            >
              <KeyRound className="w-4 h-4" strokeWidth={2.4} />
              password
            </button>
            <button
              onClick={() => loadRsvps(token)}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 bg-mushroom-light text-ink font-semibold rounded-full px-5 py-3 hover:bg-mushroom transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} strokeWidth={2.4} />
              refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 bg-ink text-oat font-semibold rounded-full px-5 py-3 hover:bg-ink/85 transition-colors"
            >
              <LogOut className="w-4 h-4" strokeWidth={2.4} />
              log out
            </button>
          </div>
        </div>

        {showPwd && (
          <form
            onSubmit={changePassword}
            data-testid="admin-change-password-form"
            className="mt-6 rounded-[1.5rem] bg-mushroom-light p-6 sm:p-8"
          >
            <div className="flex items-center justify-between">
              <h2 className="font-display font-black lowercase text-2xl text-ink">change password</h2>
              <button type="button" onClick={() => setShowPwd(false)} className="text-ink/60 hover:text-ink" aria-label="close">
                <X className="w-5 h-5" strokeWidth={2.4} />
              </button>
            </div>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <input
                type="password"
                data-testid="admin-current-password"
                value={pwd.current}
                onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
                className="bg-oat rounded-xl px-4 py-3 text-base font-medium text-ink outline-none border-2 border-transparent focus:border-ink transition-colors"
                placeholder="current password"
              />
              <input
                type="password"
                data-testid="admin-new-password"
                value={pwd.next}
                onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
                className="bg-oat rounded-xl px-4 py-3 text-base font-medium text-ink outline-none border-2 border-transparent focus:border-ink transition-colors"
                placeholder="new password"
              />
              <input
                type="password"
                data-testid="admin-confirm-password"
                value={pwd.confirm}
                onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                className="bg-oat rounded-xl px-4 py-3 text-base font-medium text-ink outline-none border-2 border-transparent focus:border-ink transition-colors"
                placeholder="confirm new password"
              />
            </div>
            {pwdMsg.text && (
              <p
                data-testid="admin-password-msg"
                className={`mt-4 text-sm font-semibold ${pwdMsg.type === "ok" ? "text-green-700" : "text-red-700"}`}
              >
                {pwdMsg.text}
              </p>
            )}
            <button
              type="submit"
              data-testid="admin-change-password-submit"
              className="mt-5 inline-flex items-center justify-center bg-sunbeam text-ink font-display font-black lowercase tracking-tight rounded-full px-7 py-3 text-base leading-none shadow-[0_5px_0_0_#1C1B18] hover:shadow-[0_3px_0_0_#1C1B18] transition-shadow"
            >
              update password
            </button>
          </form>
        )}

        {allDateLabels.length > 0 && (
          <div className="mt-8 flex flex-col gap-4">
            <div className="relative max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-ink/40" strokeWidth={2.4} />
              <input
                type="text"
                data-testid="admin-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="search by name or email"
                className="w-full bg-mushroom-light rounded-full pl-12 pr-4 py-3 text-base font-medium text-ink outline-none border-2 border-transparent focus:border-ink transition-colors placeholder:text-ink/40"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2" data-testid="admin-date-filter">
            <span className="text-sm font-semibold text-ink/60 mr-1">filter by date:</span>
            <button
              onClick={() => setFilterDate("all")}
              className={`text-sm font-semibold rounded-full px-4 py-2 transition-colors ${
                filterDate === "all" ? "bg-ink text-oat" : "bg-mushroom-light text-ink hover:bg-mushroom"
              }`}
            >
              all ({rows.length})
            </button>
            {allDateLabels.map((d) => {
              const count = rows.filter((r) => (r.dates || []).includes(d)).length;
              return (
                <button
                  key={d}
                  data-testid={`admin-filter-${d}`}
                  onClick={() => setFilterDate(d)}
                  className={`text-sm font-semibold rounded-full px-4 py-2 transition-colors ${
                    filterDate === d ? "bg-ink text-oat" : "bg-mushroom-light text-ink hover:bg-mushroom"
                  }`}
                >
                  {d} ({count})
                </button>
              );
            })}
            </div>
          </div>
        )}

        {error && (
          <p className="mt-6 text-base font-semibold text-red-700" data-testid="admin-load-error">
            {error}
          </p>
        )}

        <div className="mt-8 overflow-x-auto rounded-[1.5rem] border-2 border-ink/10 bg-white/40">
          <table className="w-full text-left border-collapse" data-testid="admin-table">
            <thead>
              <tr className="bg-mushroom-deep text-oat text-xs uppercase tracking-[0.14em]">
                <th className="px-5 py-4 font-semibold">Name</th>
                <th className="px-5 py-4 font-semibold">Email</th>
                <th className="px-5 py-4 font-semibold">Cell number</th>
                <th className="px-5 py-4 font-semibold">Dates</th>
                <th className="px-5 py-4 font-semibold">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 && !loading && (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-ink/50 font-medium">
                    No RSVPs yet.
                  </td>
                </tr>
              )}
              {filteredRows.map((r, i) => (
                <tr key={r.id || i} className="border-t border-ink/10 align-top" data-testid="admin-row">
                  <td className="px-5 py-4 font-semibold text-ink">{r.name}</td>
                  <td className="px-5 py-4 text-ink/80">{r.email}</td>
                  <td className="px-5 py-4 text-ink/80">{r.phone || "—"}</td>
                  <td className="px-5 py-4 text-ink/80">
                    <ul className="space-y-1">
                      {(r.dates || []).map((d, j) => (
                        <li key={j} className="text-sm">{d}</li>
                      ))}
                    </ul>
                  </td>
                  <td className="px-5 py-4 text-ink/60 whitespace-nowrap text-sm">{fmtDate(r.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
